// ✅ src/pages/Asset/AssetAbstractDetails.jsx  (FULL FILE - UPDATED WITH localStorage + commonHeaders)

import React, { useEffect, useMemo, useState, useContext } from "react";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaEye,
  FaFileExcel,
  FaPrint,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const API_BASE = (import.meta?.env?.VITE_API_BASE || "http://nissi-india.com").replace(
  /\/$/,
  ""
);

const AssetAbstractDetails = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ localStorage data
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  // ✅ numeric fallbacks if backend expects numbers
  const numericUserId = Number(userId) || 0;
  const numericBranchId = Number(branchId) || 0;

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    fromDate: "03/09/2025",
    toDate: "10/09/2025",
    assetType: "",
    vehicleRegNo: "",
    make: "",
    model: "",
    location: "",
    hypothecated: "",
    options: { idle: false },
  });

  const [dropdowns, setDropdowns] = useState({
    assetTypes: [],
    vehicles: [],
    makes: [],
    models: [],
    locations: [],
    hypothecated: [],
  });

  const [rights, setRights] = useState({
    hasRights: true,
    hasPrintRights: true,
  });

  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingView, setLoadingView] = useState(false);

  const [records, setRecords] = useState([]);
  const [totals, setTotals] = useState({
    purchaseValue: 0,
    depreciationValue: 0,
    wdv: 0,
  });

  // ✅ Generic mapping
  const mapDrop = (rows, { valueKey, labelKey } = {}) => {
    if (!Array.isArray(rows)) return [];
    return rows
      .map((r) => {
        const keys = Object.keys(r || {});
        const vKey =
          valueKey ||
          keys.find((k) => /^fld_.*id$/i.test(k)) ||
          keys.find((k) => /(^id$|_id$|value$)/i.test(k)) ||
          keys[0];

        const lKey =
          labelKey ||
          keys.find((k) => /(AssetType|name|text|desc|type)$/i.test(k)) ||
          keys.find((k) => /^fld_.*name$/i.test(k)) ||
          keys[1] ||
          keys[0];

        const value = r?.[vKey];
        const label = r?.[lKey];

        return {
          value: value ?? "",
          label: String(label ?? value ?? ""),
          raw: r,
        };
      })
      .filter((x) => x.label && x.label.toLowerCase() !== "select");
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleOptionChange = (option, checked) => {
    setFilters((prev) => ({
      ...prev,
      options: { ...prev.options, [option]: checked },
    }));
  };

  const buildPayload = () => ({
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    assetType: filters.assetType,
    vehicleRegNo: filters.vehicleRegNo,
    make: filters.make,
    model: filters.model,
    location: filters.location,
    idle: !!filters.options.idle,
    hypothecated: filters.hypothecated,
    finYearId,
    finYear,
    branchId,
    branchtype: branchType,
    branchType,
    userId,
    loginuserid: userId,
  });

  const buildPrintQuery = () => {
    const q = new URLSearchParams();
    q.set("assetType", filters.assetType || "");
    q.set("vehicleRegNo", filters.vehicleRegNo || "");
    q.set("fromDate", filters.fromDate || "");
    q.set("toDate", filters.toDate || "");
    q.set("make", filters.make || "");
    q.set("model", filters.model || "");
    q.set("location", filters.location || "");
    q.set("idle", filters.options.idle ? "true" : "false");
    q.set("hypothecated", filters.hypothecated || "");
    q.set("finYearId", finYearId || "");
    q.set("finYear", finYear || "");
    q.set("branchId", branchId || "");
    q.set("branchType", branchType || "");
    q.set("userId", userId || "");
    return q.toString();
  };

  // ---------- API CALLS ----------
  const loadInit = async () => {
    if (!numericUserId) {
      toast.error("UserId missing in localStorage (userId/loginuserid)");
      return;
    }

    setLoadingInit(true);
    try {
      const url = `${API_BASE}/api/abstractDetails/init?userId=${numericUserId}&branchtype=${encodeURIComponent(
        branchType
      )}&branchid=${numericBranchId}&finYearId=${encodeURIComponent(
        finYearId
      )}&finYear=${encodeURIComponent(finYear)}`;

      const res = await fetch(url, {
        method: "GET",
        headers: commonHeaders,
      });

      const data = await res.json();
      console.log("res", data);

      if (!res.ok || !data?.success) {
        toast.error(data?.message || "Failed to load dropdowns");
        return;
      }

      setRights({
        hasRights: !!data?.rights?.hasRights,
        hasPrintRights: !!data?.rights?.hasPrintRights,
      });

      setDropdowns({
        assetTypes: mapDrop(data?.dropdowns?.assetTypes || [], {
          valueKey: "fld_assettypeid",
          labelKey: "AssetType",
        }),
        vehicles: mapDrop(data?.dropdowns?.vehicles || []),
        makes: mapDrop(data?.dropdowns?.makes || [], {
          valueKey: "fld_Makeid",
          labelKey: "Make",
        }),
        models: mapDrop(data?.dropdowns?.models || [], {
          valueKey: "fld_Modelid",
          labelKey: "Model",
        }),
        locations: mapDrop(data?.dropdowns?.locations || []),
        hypothecated: mapDrop(data?.dropdowns?.hypothecated || []),
      });
    } catch (err) {
      toast.error(err?.message || "Init error");
    } finally {
      setLoadingInit(false);
    }
  };

  const handleViewClick = async () => {
    setLoadingView(true);
    try {
      const res = await fetch(`${API_BASE}/api/abstractDetails/view`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...commonHeaders,
        },
        body: JSON.stringify(buildPayload()),
      });

      const data = await res.json();
      if (!res.ok || !data?.success) {
        toast.error(data?.message || "Failed to generate report");
        return;
      }

      setRecords(Array.isArray(data?.data) ? data.data : []);
      setTotals({
        purchaseValue: Number(data?.totals?.purchaseValue || 0),
        depreciationValue: Number(data?.totals?.depreciationValue || 0),
        wdv: Number(data?.totals?.wdv || 0),
      });

      toast.success("Report generated");
    } catch (err) {
      toast.error(err?.message || "View error");
    } finally {
      setLoadingView(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/abstractDetails/excel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...commonHeaders,
        },
        body: JSON.stringify(buildPayload()),
      });

      if (!res.ok) {
        const errText = await res.text();
        toast.error(errText || "Excel export failed");
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "AssetReport.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Excel exported");
    } catch (err) {
      toast.error(err?.message || "Excel export error");
    }
  };

  const handlePrint = () => {
    window.open(`/Asset/AssetMasterReport_Print?${buildPrintQuery()}`, "_blank");
    toast.info("Opening print...");
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Table columns ----------
  const columns = useMemo(() => {
    if (records.length) {
      return Object.keys(records[0] || {}).slice(0, 12).map((k) => ({
        key: k,
        label: k,
      }));
    }

    return [
      { key: "fld_AssetType", label: "Asset Type" },
      { key: "fld_AssetId", label: "Asset ID/Code" },
      { key: "fld_Make", label: "Make" },
      { key: "fld_Model", label: "Model" },
      { key: "fld_Location", label: "Location" },
      { key: "fld_RegNo", label: "Registration" },
      { key: "fld_Status", label: "Status" },
    ];
  }, [records]);

  if (rights.hasRights === false) {
    return (
      <AssetLayoutWrapper>
        <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
          <HeaderBar
            title={<span className="font-dmserif text-3xl">ASSET DETAILS REPORT</span>}
            setSkin={setSkin}
            currentSkin={skin}
          />
          <div
            className={`p-6 mt-6 rounded-2xl shadow-xl border ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
                : "bg-white text-black"
            }`}
          >
            <div className="text-center py-10">
              <div className="text-xl font-semibold">Access Denied</div>
              <div className="mt-2 opacity-80">
                You dont have rights to access this page
              </div>
            </div>
          </div>
        </div>
      </AssetLayoutWrapper>
    );
  }

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET DETAILS REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Asset Details Report
            </h1>
          </div>

          {/* Filters */}
          <div
            className={`mb-6 rounded-lg border ${
              isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
            }`}
          >
            <div
              className={`flex justify-between items-center p-4 cursor-pointer ${
                isDark ? "hover:bg-gray-700/50" : "hover:bg-gray-100"
              }`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <h3 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Report Filters
              </h3>
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
            </div>

            {showFilters && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      From Date
                    </label>
                    <input
                      type="text"
                      value={filters.fromDate}
                      onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                      placeholder="dd/mm/yyyy"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      To Date
                    </label>
                    <input
                      type="text"
                      value={filters.toDate}
                      onChange={(e) => handleFilterChange("toDate", e.target.value)}
                      placeholder="dd/mm/yyyy"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Asset Type
                    </label>
                    <select
                      value={filters.assetType}
                      onChange={(e) => handleFilterChange("assetType", e.target.value)}
                      disabled={loadingInit}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {dropdowns.assetTypes.map((a) => (
                        <option key={a.value} value={a.value}>
                          {a.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Vehicle Reg No
                    </label>
                    <select
                      value={filters.vehicleRegNo}
                      onChange={(e) => handleFilterChange("vehicleRegNo", e.target.value)}
                      disabled={loadingInit}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {dropdowns.vehicles.map((v, idx) => (
                        <option key={idx} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Make
                    </label>
                    <select
                      value={filters.make}
                      onChange={(e) => handleFilterChange("make", e.target.value)}
                      disabled={loadingInit}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {dropdowns.makes.map((m, idx) => (
                        <option key={idx} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Options
                    </label>
                    <div className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        id="idle"
                        checked={filters.options.idle}
                        onChange={(e) => handleOptionChange("idle", e.target.checked)}
                        className="mr-2 rounded"
                      />
                      <label
                        htmlFor="idle"
                        className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Idle
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Model
                    </label>
                    <select
                      value={filters.model}
                      onChange={(e) => handleFilterChange("model", e.target.value)}
                      disabled={loadingInit}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {dropdowns.models.map((m, idx) => (
                        <option key={idx} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Location
                    </label>
                    <select
                      value={filters.location}
                      onChange={(e) => handleFilterChange("location", e.target.value)}
                      disabled={loadingInit}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {dropdowns.locations.map((l, idx) => (
                        <option key={idx} value={l.value}>
                          {l.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Hypothecated
                    </label>
                    <select
                      value={filters.hypothecated}
                      onChange={(e) => handleFilterChange("hypothecated", e.target.value)}
                      disabled={loadingInit}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {dropdowns.hypothecated.map((h, idx) => (
                        <option key={idx} value={h.value}>
                          {h.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-center gap-5">
                  <button
                    onClick={handleViewClick}
                    disabled={loadingView}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loadingView ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaEye />
                    {loadingView ? "Loading..." : "View"}
                  </button>

                  <button
                    onClick={handleExportExcel}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark
                        ? "bg-green-700 hover:bg-green-600 text-white"
                        : "bg-green-600 hover:bg-green-700 text-white"
                    }`}
                  >
                    <FaFileExcel /> Excel
                  </button>

                  {rights.hasPrintRights !== false && (
                    <button
                      onClick={handlePrint}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                        isDark
                          ? "bg-blue-700 hover:bg-blue-600 text-white"
                          : "bg-blue-600 hover:bg-blue-700 text-white"
                      }`}
                    >
                      <FaPrint /> Print
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {records.length === 0 ? (
              <div className={`p-12 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-xl font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  No Records Found
                </div>
                <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No asset records match the current filter criteria. Please adjust your filters and try again.
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                        S.No
                      </th>
                      {columns.map((c) => (
                        <th
                          key={c.key}
                          className="px-4 py-4 text-center font-semibold border-r border-white/20"
                        >
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {records.map((row, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-center">{idx + 1}</td>
                        {columns.map((c) => (
                          <td key={c.key} className="px-4 py-3 text-center">
                            {row?.[c.key] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>

                  <tfoot>
                    <tr className={isDark ? "bg-gray-800" : "bg-gray-100"}>
                      <td
                        className="px-4 py-3 text-center font-semibold"
                        colSpan={columns.length + 1}
                      >
                        Purchase: {totals.purchaseValue} &nbsp; | &nbsp; Depreciation:{" "}
                        {totals.depreciationValue} &nbsp; | &nbsp; WDV: {totals.wdv}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetAbstractDetails;